/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                                  AUTH CALLBACK HANDLER                                       ║
 * ║                        Handles OAuth redirects and email confirmations                       ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { DatabaseService } from '../lib/database-v2';

interface AuthCallbackProps {
  onAuthComplete: (isNewUser: boolean) => void;
}

export function AuthCallback({ onAuthComplete }: AuthCallbackProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const db = DatabaseService.getInstance();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('🔄 Processing auth callback...');
        
        // Handle the OAuth/email confirmation callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Auth callback error:', error.message);
          setError(error.message);
          setIsLoading(false);
          return;
        }

        if (data.session && data.session.user) {
          console.log('✅ Auth callback successful for:', data.session.user.email);
          
          // Check if this is a new user by looking for existing profile
          const existingProfile = await db.getUserProfile(data.session.user.id);
          const isNewUser = !existingProfile;
          
          if (isNewUser) {
            console.log('👤 New user detected, will redirect to onboarding');
          } else {
            console.log('👤 Returning user detected, will redirect to dashboard');
          }
          
          // Complete the authentication process
          onAuthComplete(isNewUser);
        } else {
          // No session found, redirect to login
          console.log('📝 No session found in callback, redirecting to login');
          onAuthComplete(false);
        }
      } catch (error: any) {
        console.error('❌ Auth callback processing failed:', error);
        setError(error.message || 'Authentication failed');
      } finally {
        setIsLoading(false);
      }
    };

    // Process the callback after a short delay to ensure URL params are available
    const timer = setTimeout(handleAuthCallback, 100);
    return () => clearTimeout(timer);
  }, [onAuthComplete]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Completing authentication...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait while we sign you in</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => onAuthComplete(false)}
            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // This shouldn't be reached, but just in case
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-600 text-lg">Redirecting...</p>
      </div>
    </div>
  );
}